/**
 * Single source-of-truth for what each water test covers (marketing-side mirror).
 *
 * ⚠️  CANONICAL DEFINITION lives at `/app/backend/test_catalog.py`. This file
 * MUST mirror it for the marketing pages. The lab-tech entry form fetches the
 * grouped catalog from `GET /api/lab/field-specs` (driven by the same Python
 * SSoT), so there is one logical source of truth — this file is a static
 * snapshot for fast first-paint on the homepage.
 *
 * Consumed by:
 *  - `TestComparison.jsx` (homepage "Which parameters does each test cover?" table)
 *  - `Pricing.jsx`        (homepage "Tests & Pricing" cards — expandable section)
 *
 * Keep this file ordered & grouped by category. Use `null` if a category is
 * not included for a given test (renders as an X / hidden in the cards).
 */

export const TEST_PARAMETERS = {
  basic: {
    // Feb 2026 — customer-facing rename. See /app/frontend/src/lib/productBranding.js
    // for the canonical AWARE product family definitions.
    name: "AWARE 4-Point",
    short: "Checks for E. coli and Coliforms presence, plus pH and conductivity.",
    groups: {
      "Microbiological": [
        "E. coli (Positive/Negative)",
        "Coliforms (Positive/Negative)",
      ],
      "Basic Indicators": ["pH", "Conductivity"],
    },
  },
  tank: {
    name: "AWARE Tank 360",
    short:
      "Comprehensive tank water testing covering microbiological, chemical, mineral, anion and metal indicators.",
    groups: {
      "Microbiological": [
        "E. coli (Positive/Negative)",
        "Coliforms (Positive/Negative)",
      ],
      "Chemical Parameters": [
        "pH", "TDS", "Conductivity", "Turbidity", "Temperature",
        "Alkalinity", "Total Hardness", "Calcium Hardness",
      ],
      "Cations — Soluble": ["Calcium", "Magnesium"],
      "Anions — Soluble": ["Chloride", "Nitrate", "Ammonia", "Phosphate", "Sulphate"],
      // Feb 2026 — Manganese intentionally removed from the AWARE Tank 360
      // package. It remains in AWARE Bore 360 below. Kept in
      // COMPARISON_CATEGORIES because the "Metals" row still appears —
      // just with a shorter list on the tank column.
      "Metals": ["Iron", "Copper"],
    },
  },
  bore: {
    name: "AWARE Bore 360",
    short:
      "Comprehensive bore water testing covering microbiological, chemical, mineral, anion and metals analysis.",
    groups: {
      "Microbiological": [
        "E. coli (Positive/Negative)",
        "Coliforms (Positive/Negative)",
      ],
      "Chemical Parameters": [
        "pH", "TDS", "Conductivity", "Turbidity", "Temperature",
        "Alkalinity", "Total Hardness", "Calcium Hardness",
      ],
      "Cations — Soluble": ["Calcium", "Magnesium"],
      "Anions — Soluble": [
        "Chloride", "Nitrate", "Nitrite", "Ammonia", "Phosphate", "Sulphate",
      ],
      "Metals": [
        "Manganese", "Iron", "Copper",
      ],
    },
  },
};

/**
 * Categories used in the comparison-table rows. Order matters — drives the
 * row sequence in `TestComparison.jsx`.
 */
export const COMPARISON_CATEGORIES = [
  "Microbiological",
  "Chemical Parameters",
  "Cations — Soluble",
  "Anions — Soluble",
  "Metals",
];

/**
 * Returns the parameter list for a given category in a given test, or `null`
 * if the test does not include that category. Used by the comparison table.
 *
 * Special case: the Basic test's chemistry lives under "Basic Indicators" but
 * the comparison-table groups it under "Chemical Parameters" — we map it here.
 */
export function getCategoryParams(testId, category) {
  const test = TEST_PARAMETERS[testId];
  if (!test) return null;
  if (category === "Chemical Parameters" && testId === "basic") {
    return test.groups["Basic Indicators"] || null;
  }
  return test.groups[category] || null;
}
